import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ADMIN_API_BASE } from "./adminApiConfig";
import {
  clearAdminSession,
  getAccessToken,
  getRefreshToken,
  updateAdminTokens,
} from "./adminAuth";

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

const adminAxios = axios.create({
  headers: { "Content-Type": "application/json" },
});

const isAuthFree = (url = ""): boolean =>
  url.includes("/api/admin/login") || url.includes("/api/admin/refresh");

let refreshInFlight: Promise<boolean> | null = null;

const redirectToLogin = (): void => {
  clearAdminSession();
  if (window.location.pathname !== "/admin/login") {
    window.location.href = "/admin/login";
  }
};

const refreshAccessToken = async (): Promise<boolean> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await axios.post(`${ADMIN_API_BASE}/api/admin/refresh`, {
        refreshToken,
      });
      const body = response.data;
      const accessToken = body?.data?.accessToken;
      if (body?.code === 200 && accessToken) {
        updateAdminTokens(accessToken, body.data.refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

adminAxios.interceptors.request.use((config) => {
  if (!isAuthFree(config.url)) {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

const retryAfterRefresh = async (
  original: RetryConfig
): Promise<AxiosResponse> => {
  if (original._retry) {
    redirectToLogin();
    return Promise.reject(new Error("Admin session expired"));
  }

  original._retry = true;
  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    redirectToLogin();
    return Promise.reject(new Error("Admin session expired"));
  }

  return adminAxios.request(original);
};

adminAxios.interceptors.response.use(
  (response) => {
    const url = String(response.config.url || "");
    if (
      !isAuthFree(url) &&
      response.data?.code === 401 &&
      !(response.config as RetryConfig)._retry
    ) {
      return retryAfterRefresh(response.config as RetryConfig);
    }
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const status = error.response?.status;
    const url = String(original?.url || "");

    if (status === 401 && original && !isAuthFree(url)) {
      return retryAfterRefresh(original);
    }

    return Promise.reject(error);
  }
);

const adminRequest = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const response = await adminAxios.request<T>(config);
  return response.data;
};

export default adminRequest;
